import React from "react";
import Box from "@mui/material/Box";
import { keyframes } from "@emotion/react";
import { alpha, useTheme } from "@mui/material/styles";

const drawLine = keyframes`
  0% { stroke-dashoffset: 1; opacity: 0; }
  8% { opacity: 1; }
  45%, 85% { stroke-dashoffset: 0; opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0; }
`;

const fadeArea = keyframes`
  0%, 30% { opacity: 0; }
  50%, 85% { opacity: 1; }
  100% { opacity: 0; }
`;

const barRise = keyframes`
  0%, 100% { transform: scaleY(0.35); }
  50% { transform: scaleY(1); }
`;

const donutFill = keyframes`
  0% { stroke-dashoffset: 1; }
  40%, 80% { stroke-dashoffset: var(--donut-end); }
  100% { stroke-dashoffset: 1; }
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.6; }
  100% { transform: scale(3.2); opacity: 0; }
`;

const blink = keyframes`
  0%, 100% { opacity: 0.35; }
  50% { opacity: 1; }
`;

// Bar chart clusters: [x, baseline y, bar heights]
const BAR_GROUPS = [
  {
    x: 70,
    y: 860,
    width: 22,
    gap: 12,
    heights: [90, 140, 110, 180, 130, 210, 160],
  },
  { x: 1180, y: 300, width: 16, gap: 10, heights: [60, 95, 75, 120, 100, 140] },
];

// Map pins representing health facilities / regions
const PINS = [
  { x: 1240, y: 640 },
  { x: 1330, y: 560 },
  { x: 1150, y: 720 },
  { x: 180, y: 360 },
  { x: 260, y: 470 },
];

// Points along the main trend line that blink like live data
const TREND_POINTS = [
  { x: 240, y: 560 },
  { x: 600, y: 470 },
  { x: 960, y: 420 },
  { x: 1320, y: 290 },
];

// Faint, slowly animated chart shapes behind the hero content
export default function HeroDataBackground() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const secondary = theme.palette.secondary.main;
  const accent = "#ff9800";

  return (
    <Box
      aria-hidden
      component="svg"
      viewBox="0 0 1440 900"
      preserveAspectRatio="xMidYMid slice"
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        zIndex: -2,
        pointerEvents: "none",
        "& .line": {
          strokeDasharray: 1,
          animation: `${drawLine} 14s ease-in-out infinite`,
        },
        "& .area": { animation: `${fadeArea} 14s ease-in-out infinite` },
        "& .bar": {
          transformBox: "fill-box",
          transformOrigin: "bottom",
          animation: `${barRise} 4s ease-in-out infinite`,
        },
        "& .donut": {
          strokeDasharray: 1,
          transform: "rotate(-90deg)",
          transformBox: "fill-box",
          transformOrigin: "center",
          animation: `${donutFill} 10s ease-in-out infinite`,
        },
        "& .pulse": {
          transformBox: "fill-box",
          transformOrigin: "center",
          animation: `${pulse} 2.8s ease-out infinite`,
        },
        "& .point": { animation: `${blink} 3s ease-in-out infinite` },
        "@media (prefers-reduced-motion: reduce)": {
          "& *": { animation: "none !important" },
          "& .line": { strokeDashoffset: 0 },
        },
      }}
    >
      <defs>
        <linearGradient id="hero-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={alpha(primary, 0.14)} />
          <stop offset="100%" stopColor={alpha(primary, 0)} />
        </linearGradient>
      </defs>

      {/* Chart gridlines */}
      {[180, 360, 540, 720].map((y) => (
        <line
          key={y}
          x1="0"
          x2="1440"
          y1={y}
          y2={y}
          stroke={alpha(primary, 0.07)}
          strokeDasharray="6 10"
        />
      ))}

      {/* Main trend line with area fill */}
      <path
        className="area"
        d="M0,610 C120,600 180,570 240,560 C360,540 480,500 600,470 C720,440 840,450 960,420 C1080,390 1200,330 1320,290 C1380,270 1420,260 1440,255 L1440,900 L0,900 Z"
        fill="url(#hero-area)"
      />
      <path
        className="line"
        pathLength="1"
        d="M0,610 C120,600 180,570 240,560 C360,540 480,500 600,470 C720,440 840,450 960,420 C1080,390 1200,330 1320,290 C1380,270 1420,260 1440,255"
        fill="none"
        stroke={alpha(primary, 0.35)}
        strokeWidth="3"
        strokeLinecap="round"
      />
      {TREND_POINTS.map((p, i) => (
        <circle
          key={i}
          className="point"
          cx={p.x}
          cy={p.y}
          r="6"
          fill="#fff"
          stroke={alpha(primary, 0.5)}
          strokeWidth="3"
          style={{ animationDelay: `${i * 0.6}s` }}
        />
      ))}

      {/* Secondary comparison line */}
      <path
        className="line"
        pathLength="1"
        d="M0,720 C160,700 300,690 420,650 C560,605 700,640 840,600 C980,560 1100,580 1240,520 C1320,486 1400,470 1440,465"
        fill="none"
        stroke={alpha(secondary, 0.3)}
        strokeWidth="2.5"
        strokeLinecap="round"
        style={{ animationDelay: "-5s" }}
      />

      {/* Bar charts */}
      {BAR_GROUPS.map((group, g) =>
        group.heights.map((height, i) => (
          <rect
            key={`${g}-${i}`}
            className="bar"
            x={group.x + i * (group.width + group.gap)}
            y={group.y - height}
            width={group.width}
            height={height}
            rx="4"
            fill={alpha(g === 0 ? primary : secondary, 0.13)}
            style={{ animationDelay: `${i * 0.25 + g}s` }}
          />
        )),
      )}

      {/* Donut chart */}
      <g transform="translate(120 170)">
        <circle
          r="56"
          fill="none"
          stroke={alpha(primary, 0.08)}
          strokeWidth="16"
        />
        <circle
          className="donut"
          pathLength="1"
          r="56"
          fill="none"
          stroke={alpha(accent, 0.35)}
          strokeWidth="16"
          strokeLinecap="round"
          style={{ "--donut-end": 0.3 }}
        />
      </g>
      <g transform="translate(1330 140)">
        <circle
          r="34"
          fill="none"
          stroke={alpha(secondary, 0.08)}
          strokeWidth="10"
        />
        <circle
          className="donut"
          pathLength="1"
          r="34"
          fill="none"
          stroke={alpha(secondary, 0.3)}
          strokeWidth="10"
          strokeLinecap="round"
          style={{ "--donut-end": 0.45, animationDelay: "-3s" }}
        />
      </g>

      {/* Facility pins with pulsing rings */}
      {PINS.map((pin, i) => (
        <g key={i} transform={`translate(${pin.x} ${pin.y})`}>
          <circle
            className="pulse"
            r="8"
            fill={alpha(accent, 0.35)}
            style={{ animationDelay: `${i * 0.55}s` }}
          />
          <circle r="5" fill={alpha(accent, 0.6)} />
        </g>
      ))}
    </Box>
  );
}
